<template>
  <div class="responsive-crud-page">
    <KTCard ref="preview" v-bind:title="'Add new'">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-staff' }">{{
            $t("MENU.ITEM.SETTINGS.STAFF")
          }}</router-link>
          <i class="mr-2"></i>
          <small class="">{{ $t("LABELS.ADD_NEW") }}</small>
        </h3>
      </template>
      <template v-slot:toolbar>
        <div class="responsive-crud-page__actions">
          <BackButton></BackButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingStaff"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
      <template v-slot:body>
        <b-form>
          <FormBuilder
            v-if="!firstLoader"
            :form="form"
            :schemaJson="schemaJson"
            :v="$v"
          ></FormBuilder>

          <v-overlay :value="!firstLoader && isLoadingStaff">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </v-overlay>
        </b-form>
      </template>
      <template v-slot:foot>
        <div
          v-show="_.size(errors) > 0"
          role="alert"
          class="alert alert-danger"
        >
          <div class="alert-text" v-for="(error, i) in errors" :key="i">
            {{ error }}
          </div>
        </div>
      </template>
      <template v-slot:toolbarfoot>
        <div class="responsive-crud-page__actions">
          <BackButton></BackButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingStaff"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
    </KTCard>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import ApiService from "@/core/services/api.service";
import { SAVE_STAFF } from "@/modules/staff/store/staff.module";

import FormBuilder from "@/view/content/forms/FormBuilder.vue";
import FormInput from "@/view/content/forms/components/FormInput.vue";
import SaveButtonDropdown from "@/view/content/forms/components/SaveButtonDropdown.vue";

import { validationMixin } from "vuelidate";

import { formBuilderMixin } from "@/core/mixins/formBuilderMixin.js";

import KTCard from "@/view/content/Card.vue";
import { mapGetters, mapState } from "vuex";

export default {
  mixins: [validationMixin, formBuilderMixin],
  data() {
    return {
      errors: {},
      firstLoader: true,
      staff: {},
      staffTypes: []
    };
  },
  components: {
    KTCard,
    FormBuilder,
    FormInput,
    SaveButtonDropdown
  },
  mounted() {
    let vm = this;
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.ASSETS.STAFF"),
        route: { name: "list-staff" }
      },
      { title: this.$i18n.t("LABELS.ADD_NEW") }
    ]);
    vm.generateFormOutOfSchemaJson(this.schemaJson);

    ApiService.get("staff-types").then(({ data }) => {
      vm.staffTypes = data.data;
    });

    vm.$nextTick(function() {
      vm.firstLoader = false;
    });
  },
  watch: {
    isActiveDriver(value) {
      if (!value) {
        this.form.driverPassword = "";
      }
    }
  },
  computed: {
    ...mapGetters(["isLoadingStaff"]),
    ...mapState({
      errors: state => state.auth.errors
    }),
    isActiveDriver() {
      const staffType = this.staffTypes.find(
        type => Number(type.id) === Number(this.form.staffTypeId)
      );

      return Boolean(this.form.active) && staffType?.slug === "driver";
    },
    schemaJson() {
      return {
        fields: [
          {
            type: "input",
            inputType: "text",
            id: "staff-firstName-input",
            groupId: "staff-firstName-group",
            required: "required",
            feedback_id: "staff-live-feedback",
            colMd: 4,
            divider: false,
            i18n: {
              label: "FORM_LABELS.FIRST_NAME",
              placeholder: "FORM_PLACEHOLDERS.FIRST_NAME",
              validation: "FORM.VALID_FIRST_NAME"
            },
            validations: {
              required: true,
              minLength: 2,
              params: { name: "First Name" }
            },
            translatable: false,
            model: "firstName"
          },
          {
            type: "input",
            inputType: "text",
            id: "staff-lastName-input",
            groupId: "staff-lastName-group",
            required: "required",
            feedback_id: "staff-live-feedback",
            colMd: 4,
            divider: false,
            i18n: {
              label: "FORM_LABELS.LAST_NAME",
              placeholder: "FORM_PLACEHOLDERS.LAST_NAME",
              validation: "FORM.VALID_LAST_NAME"
            },
            validations: {
              required: true,
              minLength: 2
            },
            translatable: false,
            model: "lastName"
          },
          {
            type: "input",
            inputType: "number",
            id: "staff-hourlyRate-input",
            groupId: "staff-hourlyrate-group",
            required: "required",
            feedback_id: "staff-live-feedback",
            colMd: 4,
            divider: false,
            i18n: {
              label: "MENU.ITEM.STAFF.HOURLY_RATE",
              placeholder: "MENU.ITEM.STAFF.PLACEHOLDER_HOURLY_RATE",
              validation: "MENU.ITEM.STAFF.HOURLY_RATE"
            },
            validations: {
              required: true,
              minLength: 1
            },
            translatable: false,
            model: "hourlyRate"
          },
          {
            type: "input",
            inputType: "text",
            id: "staff-phone-input",
            groupId: "staff-phone-group",
            required: "required",
            feedback_id: "staff-live-feedback",
            colMd: 4,
            divider: true,
            i18n: {
              label: "FORM_LABELS.PHONE",
              placeholder: "FORM_PLACEHOLDERS.PHONE",
              validation: "FORM.VALID_PHONE"
            },
            validations: {
              minLength: 10,
              maxLength: 10
            },
            translatable: false,
            model: "phone"
          },
          {
            type: "select",
            inputType: "select",
            id: "staff-staff-type-input",
            groupId: "staff-staff-type-group",
            feedback_id: "staff-staff-type-live-feedback",
            colMd: 4,
            divider: false,
            clearable: true,
            i18n: {
              label: "MENU.ITEM.STAFF.STAFF_TYPE",
              placeholder: "MENU.ITEM.STAFF.STAFF_TYPE"
            },
            options: this.staffTypes,
            validations: {},
            translatable: false,
            model: "staffTypeId"
          },
          {
            type: "switch",
            groupId: "staff-active-group",
            id: "staff-active-input",
            inputType: "checkbox",
            required: "required",
            feedback_id: "active-live-feedback",
            colMd: 4,
            divider: false,
            default: true,
            i18n: {
              label: "FORM_LABELS.ACTIVE",
              placeholder: "FORM_PLACEHOLDERS.ACTIVE",
              validation: "FORM.VALID_ACTIVE"
            },
            validations: {
              required: true
            },
            model: "active"
          },
          {
            type: "input",
            inputType: "password",
            id: "staff-driver-password-input",
            groupId: "staff-driver-password-group",
            feedback_id: "staff-driver-password-feedback",
            colMd: 4,
            divider: true,
            hidden: !this.isActiveDriver,
            i18n: {
              label: "AUTH.INPUT.PASSWORD",
              placeholder: "AUTH.INPUT.PASSWORD"
            },
            validations: {},
            translatable: false,
            model: "driverPassword"
          }
        ]
      };
    }
  },
  validations() {
    let vm = this;
    let tmpValidationObject = this.generateFormValidationsOutOfSchemaJson(
      this.schemaJson
    );

    return tmpValidationObject;
  },
  methods: {
    transformUrlParams(params) {
      let apiParams =
        "?" +
        Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&");
      return apiParams;
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    onSubmit(evt) {
      evt.preventDefault();
    },
    onSave(next) {
      let vm = this;
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> Warning",
          text: "Please fill all required fields correctly"
        });
        return;
      }
      this.$store
        .dispatch(SAVE_STAFF, this.form)
        .then(data => {
          vm.errors = data.data.errors;
          let itemEdit = data.data;
          vm.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });
          // let itemEdit = data.data;
          if (next == "continue") {
            //New
            this.$router.push({
              name: "edit-staff",
              params: { id: itemEdit.id }
            });
          }
          if (next == "new") {
            //New
            this.$router.push({ name: "add-staff" });
          }
          if (next == "exit") {
            this.$router.push({ name: "list-staff" });
          }
        })
        .catch(response => {
          vm.errors = response.data.errors;
          vm.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: response.data.message
          });
        });
    },
    resetVForm() {
      this.$v.$reset();
    }
  }
};
</script>
